import { useCallback, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import type { Session } from '@supabase/supabase-js';
import type { PlanEntitlementsRow } from '../feature-flags/domain/planEntitlements';
import { isBillingEnabled } from '../billing/isBillingEnabled';
import {
  clearBillingHandoffFromLocation,
  consumeBillingWebHandoff,
  readBillingHandoffFromLocation,
} from '../billing/billingWebHandoff';
import {
  clearPlanBillingParamsFromLocation,
  normalizePaidPlanCode,
  type PaidPlanCode,
} from '../feature-flags/planIntentService';
import { creceEmployeeGateCopy } from '../../../../../shared/plan-crece-copy';
import { showInfo } from '../../shared/ui/appToast';
import { trackCreceFunnel } from '../billing/trackCreceFunnel';
import { CRECE_FUNNEL_EVENTS } from '../../../../../shared/crece-funnel';

export type CreceRelayTarget = 'ActivateCrece' | 'CreceSuccess';

export function useBillingRelay({
  session,
  bootstrapping,
  canRelayToSubscriptionPortal,
  entitlements: _entitlements,
  isEmployee,
  planParam,
  billingFlag,
  setPlanParam,
  setBillingFlag,
  onNavigateToCrece,
}: {
  session: Session | null;
  bootstrapping: boolean;
  canRelayToSubscriptionPortal: boolean;
  entitlements: PlanEntitlementsRow | null;
  isEmployee: boolean | null;
  planParam: string | null;
  billingFlag: boolean;
  setPlanParam: (value: string | null) => void;
  setBillingFlag: (value: boolean) => void;
  onNavigateToCrece: (target: CreceRelayTarget, planCode: PaidPlanCode | null) => void;
}): () => void {
  const billingHandoffDoneRef = useRef(false);
  const billingNavigationDoneRef = useRef(false);

  useEffect(() => {
    if (Platform.OS !== 'web' || billingHandoffDoneRef.current) return;
    const code = readBillingHandoffFromLocation();
    if (!code) return;
    billingHandoffDoneRef.current = true;
    let cancelled = false;
    void consumeBillingWebHandoff(code).then((res) => {
      if (cancelled) return;
      if (res.ok) {
        console.info('[billing-handoff] consume ok');
        if (res.plan) setPlanParam(res.plan);
      } else {
        console.warn('[billing-handoff] consume failed', res.error);
      }
      setBillingFlag(true);
      clearBillingHandoffFromLocation();
    });
    return () => {
      cancelled = true;
    };
  }, [setBillingFlag, setPlanParam]);

  const navigateToCreceOnce = useCallback(
    (target: CreceRelayTarget = 'ActivateCrece', planCode: PaidPlanCode | null = null) => {
      if (!isBillingEnabled()) return;
      if (billingNavigationDoneRef.current) return;
      billingNavigationDoneRef.current = true;
      onNavigateToCrece(target, planCode);
    },
    [onNavigateToCrece],
  );

  useEffect(() => {
    if (!session?.user?.id) {
      billingNavigationDoneRef.current = false;
    }
  }, [session?.user?.id]);

  useEffect(() => {
    if (!session || bootstrapping || !canRelayToSubscriptionPortal) {
      return;
    }

    const mpReturn =
      Platform.OS === 'web' && typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('mp')
        : null;

    // Abrir Activar / Success solo por intención de esta sesión o retorno MP.
    // Pending en DB no secuestra Inicio en cold start.
    const shouldOpen = Boolean(
      isBillingEnabled() &&
        (billingFlag ||
          Boolean(planParam) ||
          mpReturn === 'success' ||
          mpReturn === 'pending'),
    );

    if (!shouldOpen) {
      return;
    }

    if (isEmployee === true) {
      showInfo(creceEmployeeGateCopy.title, creceEmployeeGateCopy.body);
      billingNavigationDoneRef.current = true;
      clearPlanBillingParamsFromLocation();
      if (planParam) setPlanParam(null);
      if (billingFlag) setBillingFlag(false);
      return;
    }

    const target: CreceRelayTarget =
      mpReturn === 'success' || mpReturn === 'pending' ? 'CreceSuccess' : 'ActivateCrece';

    if (target === 'ActivateCrece') {
      trackCreceFunnel(CRECE_FUNNEL_EVENTS.ctaClick, { source: 'relay' });
    }

    navigateToCreceOnce(target, normalizePaidPlanCode(planParam));
    clearPlanBillingParamsFromLocation();
    if (planParam) {
      setPlanParam(null);
    }
    if (billingFlag) {
      setBillingFlag(false);
    }
  }, [
    planParam,
    billingFlag,
    session,
    bootstrapping,
    canRelayToSubscriptionPortal,
    isEmployee,
    navigateToCreceOnce,
    setPlanParam,
    setBillingFlag,
  ]);

  return () => navigateToCreceOnce('ActivateCrece');
}
